// NOVELTY "heat-score" scorer (NON-SCIENTIFIC, for entertainment only).
//
// IMPORTANT: This is a playful, speculative feature that estimates a whimsical
// "on heat / estrus likelihood" percentage for a detected WILD MAMMAL. It is
// NOT science, NOT medicine, NOT veterinary advice, and it applies to ANIMALS
// ONLY, never to humans. Every result carries a disclaimer and must always be
// presented as a novelty.
//
// The maths here is intentionally simple and deterministic. It loads a set of
// speculative "factors" (distilled from open web snippets by
// server/heat_research.py, see public/heat_factors.json), maps each factor's
// "observable" bucket to a coarse visual feature the phone can estimate, weights
// the contributions, and folds in a stable per-animal hash so different animals
// get different (but repeatable) numbers. No Math.random (banned in this repo);
// all "variation" comes from hashing id + species.

import type { BBox } from "../types";

/** One speculative factor loaded from /heat_factors.json. */
export interface HeatFactor {
  id: number;
  name: string;
  description: string;
  source_url: string;
  source_title: string;
  /** 0..1 whimsical weight. */
  weight: number;
  /** Which coarse visual feature this factor (loosely) maps to. */
  observable: "eye" | "face" | "posture" | "motion" | "color" | "none";
}

/** Result of a single novelty scoring call. */
export interface HeatScore {
  /** 0..100 playful percentage. */
  percent: number;
  /** Playful band derived purely from percent ("low" | "stirring" | ...). */
  label: string;
  /** The novelty warning string. Always present. */
  disclaimer: string;
  /** Top contributing factors (name + 0..1 contribution), for display. */
  factors: { name: string; contribution: number }[];
}

/** Shape of the JSON file produced by server/heat_research.py. */
interface HeatFactorsFile {
  generated_note?: string;
  disclaimer?: string;
  factors?: HeatFactor[];
}

/** The novelty warning shown alongside every score. */
const DISCLAIMER =
  "NON-SCIENTIFIC NOVELTY. For entertainment only, not a real biological " +
  "measurement. Applies to animals only, never humans.";

/** Cached factor list (empty until loadHeatFactors resolves). */
let FACTORS: HeatFactor[] = [];
/** Guards against loading more than once. */
let loadPromise: Promise<void> | null = null;

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/**
 * Loads /heat_factors.json once and caches it. Safe to call repeatedly (returns
 * the same in-flight promise). If the fetch fails the cache stays empty and
 * scoreHeat still returns a valid neutral result (never throws).
 */
export async function loadHeatFactors(): Promise<void> {
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const res = await fetch("/heat_factors.json", { cache: "force-cache" });
      if (!res.ok) return;
      const data = (await res.json()) as HeatFactorsFile;
      if (Array.isArray(data.factors)) {
        // Keep only well-formed entries so a malformed file cannot break scoring.
        FACTORS = data.factors.filter(
          (f) => typeof f?.weight === "number" && typeof f?.observable === "string",
        );
      }
    } catch {
      // Swallow: novelty feature must never break the app. Neutral score used.
    }
  })();
  return loadPromise;
}

/**
 * Small deterministic string hash (FNV-1a, 32-bit). Used to derive stable
 * per-animal variation from id + species, replacing any need for randomness.
 */
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // 32-bit FNV prime multiply via shifts (keeps it in 32-bit range).
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/** Map a 32-bit hash to a 0..1 float. */
function hashUnit(s: string): number {
  return hashStr(s) / 0xffffffff;
}

/**
 * Coarse visual features (each 0..1) derived from the animal's detection.
 * These are deliberately crude: the whole feature is a novelty.
 */
interface CoarseFeatures {
  eye: number;
  face: number;
  posture: number;
  motion: number;
  color: number;
}

/**
 * Derive the coarse 0..1 features from the detection input. We only have a
 * bounding box, a species label, a confidence, a headFacing hint and a motion
 * hint, so each observable bucket is a whimsical blend of those plus a stable
 * per-animal hash so two animals never score identically for no reason.
 */
function coarseFeatures(
  input: { id: number; species: string; confidence: number; headFacing: number; bbox: BBox; motion: number },
): CoarseFeatures {
  const seed = `${input.species}#${input.id}`;
  const headFacing = clamp01(input.headFacing);
  const motion = clamp01(input.motion);
  const conf = clamp01(input.confidence);

  // Box aspect ratio as a loose "posture" proxy (taller boxes read as more
  // upright / presenting). Guard against a zero-height box.
  const h = input.bbox.h > 1e-4 ? input.bbox.h : 1e-4;
  const aspect = clamp01(h / (input.bbox.w + h)); // 0..1, ~0.5 for square.

  // Per-bucket hashed jitter (stable, small) so each animal has personality.
  const jEye = hashUnit(seed + ":eye");
  const jFace = hashUnit(seed + ":face");
  const jColor = hashUnit(seed + ":color");

  return {
    // Eye cues (gaze/pupil folklore): mostly how much the head faces us.
    eye: clamp01(0.55 * headFacing + 0.25 * conf + 0.20 * jEye),
    // Face cues (flush/symmetry folklore): head facing plus a hashed tint.
    face: clamp01(0.50 * headFacing + 0.30 * jFace + 0.20 * conf),
    // Posture cues: box aspect plus a hashed lean.
    posture: clamp01(0.60 * aspect + 0.40 * hashUnit(seed + ":posture")),
    // Motion cues: the caller's motion hint, lightly hashed.
    motion: clamp01(0.75 * motion + 0.25 * hashUnit(seed + ":motion")),
    // Colour cues (coat gloss/flush folklore): purely hashed (camera colour is
    // not reliably comparable), gently biased by confidence.
    color: clamp01(0.70 * jColor + 0.30 * conf),
  };
}

/**
 * Produce a deterministic novelty "heat" score for one detected animal.
 *
 * NON-SCIENTIFIC. Returns a 0..100 percent, a playful band label, the standard
 * disclaimer, and the top ~5 contributing factor names. NEVER throws: if
 * factors are not loaded yet it returns a neutral, hash-only result.
 */
export function scoreHeat(
  input: { id: number; species: string; confidence: number; headFacing: number; bbox: BBox; motion: number },
): HeatScore {
  const feats = coarseFeatures(input);
  const seed = `${input.species}#${input.id}`;

  // If no factors loaded, return a neutral-but-stable result rather than 0, so
  // the UI has something to show. Still fully disclaimed, still deterministic.
  if (FACTORS.length === 0) {
    const neutral = Math.round(30 + 40 * hashUnit(seed + ":neutral"));
    return { percent: neutral, label: bandFor(neutral), disclaimer: DISCLAIMER, factors: [] };
  }

  // Accumulate weighted contributions. Each factor contributes
  // weight * featureForItsObservable. "none" factors contribute a small hashed
  // trickle (they cannot be seen by a camera, so they barely move the number).
  let weightedSum = 0;
  let weightTotal = 0;
  const contributions: { name: string; contribution: number }[] = [];

  for (const f of FACTORS) {
    const featureVal =
      f.observable === "none"
        ? 0.15 * hashUnit(seed + ":" + f.id)
        : feats[f.observable];
    const w = clamp01(f.weight);
    const contribution = w * featureVal; // 0..1
    weightedSum += contribution;
    weightTotal += w;
    contributions.push({ name: f.name, contribution });
  }

  // Normalise to 0..1 by total weight (so more factors do not inflate the score),
  // then fold in a small stable per-animal offset for personality.
  const normalised = weightTotal > 0 ? weightedSum / weightTotal : 0;
  const personality = (hashUnit(seed + ":final") - 0.5) * 0.10; // +/-5 percent.
  const percent = Math.round(clamp01(normalised + personality) * 100);

  // Top ~5 contributors by contribution, for a playful "why" list.
  contributions.sort((a, b) => b.contribution - a.contribution);
  const top = contributions.slice(0, 5).map((c) => ({
    name: c.name,
    contribution: Math.round(c.contribution * 1000) / 1000,
  }));

  return { percent, label: bandFor(percent), disclaimer: DISCLAIMER, factors: top };
}

/** Playful band from percent alone (no other inputs). */
function bandFor(percent: number): string {
  if (percent < 25) return "low";
  if (percent < 50) return "stirring";
  if (percent < 75) return "elevated";
  return "peak";
}
